import {
  ReactElement,
  useCallback,
  useMemo,
  DragEvent,
} from 'react';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { NavigateNext as BreadcrumbsSeparator } from '@mui/icons-material';
import { Box } from '@mui/material';
import { Color } from 'shared/utils/colors';
import { useAppNavigation } from 'contexts/navigation';
import { useProjectId } from 'store/project';
import {
  getDroppedItems,
  isExternalFilesDrag,
} from 'helpers/externalDragAndDrop';
import { isInternalDataDrag } from 'helpers/internalDragAndDrop';
import { emitDocumentationDnDTargetChange } from '../DocumentationDataGrid/model';
import { useDocumentationStore } from 'store/documentation';
import { signalNavigationOutsideDocumentation } from 'store/documentation/hooks/useDocumentationUrlNavigation';
import {
  breadcrumbsContainerSx,
  breadcrumbItemSx,
  breadcrumbLabelSx,
  breadcrumbsSeparatorSx,
} from './styles';

interface BreadcrumbItem {
  _id: string;
  name: string;
}

export function DocumentationBreadcrumbs(): ReactElement {
  const intl = useIntl();
  const projectId = useProjectId();
  
  const breadcrumbs = useDocumentationStore.use.breadcrumbs();
  const searchPhrase = useDocumentationStore.use.searchPhrase();
  const navigateToDirectory = useDocumentationStore.use.navigateToDirectory();
  const navigateToRoot = useDocumentationStore.use.navigateToRoot();
  const closeGallery = useDocumentationStore.use.closeGallery();
  const moveSelectedToDirectory = useDocumentationStore.use.moveSelectedToDirectory();
  const addDirectoriesFromFiles = useDocumentationStore.use.addDirectoriesFromFiles();
  const addDocumentationsFromFiles = useDocumentationStore.use.addDocumentationsFromFiles();
  
  const { goToDocumentation } = useAppNavigation();
  
  const rootBreadcrumb: BreadcrumbItem = useMemo(() => ({
    _id: '',
    name: formatTranslation(intl, CommonTranslationKeys.documentation),
  }), [intl]);

  const displayBreadcrumbs = useMemo((): BreadcrumbItem[] => {
    if (searchPhrase?.trim()) {
      return [];
    }
    return [rootBreadcrumb, ...breadcrumbs];
  }, [searchPhrase, rootBreadcrumb, breadcrumbs]);

  const onClick = useCallback(
    (id: string, name: string) => {
      if (!id) {
        // Prevent gallery-close URL sync from fighting root navigation.
        signalNavigationOutsideDocumentation();
        closeGallery();
        goToDocumentation(projectId, { replace: true });
        navigateToRoot();
        return;
      }
      navigateToDirectory(id, name);
    },
    [
      navigateToDirectory,
      navigateToRoot,
      goToDocumentation,
      closeGallery,
      projectId,
    ]
  );

  const onDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, breadcrumb: BreadcrumbItem) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.style.backgroundColor = Color.primaryOrangeWithOpacity;

      emitDocumentationDnDTargetChange({
        id: breadcrumb._id,
        name: breadcrumb.name,
      });
    },
    []
  );

  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = 'unset';
  }, []);

  const onDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>, breadcrumb: BreadcrumbItem) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.style.backgroundColor = 'unset';

      if (isInternalDataDrag(e)) {
        moveSelectedToDirectory(breadcrumb);
        return;
      }

      if (!isExternalFilesDrag(e)) {
        return;
      }

      const { files, hasDirectories, emptyDirectoryPaths } = await getDroppedItems(e);
      if (!files.length && !emptyDirectoryPaths.length) {
        return;
      }

      if (hasDirectories) {
        addDirectoriesFromFiles(files, breadcrumb._id, emptyDirectoryPaths);
        return;
      }

      addDocumentationsFromFiles(files, breadcrumb._id);
    },
    [moveSelectedToDirectory, addDirectoriesFromFiles, addDocumentationsFromFiles]
  );

  return (
    <Box sx={breadcrumbsContainerSx}>
      {displayBreadcrumbs.map((breadcrumb, index, array) => (
        <Box
          key={breadcrumb._id || 'documentation-root'}
          onDragOver={(e) => onDragOver(e, breadcrumb)}
          onDragLeave={onDragLeave}
          onDrop={(e) => void onDrop(e, breadcrumb)}
          sx={breadcrumbItemSx}
        >
          <Box
            component='p'
            sx={breadcrumbLabelSx(index + 1 === array.length)}
            onClick={() => onClick(breadcrumb._id, breadcrumb.name)}
          >
            {breadcrumb.name}
          </Box>
          {index + 1 < array.length && (
            <BreadcrumbsSeparator sx={breadcrumbsSeparatorSx} fontSize='small' />
          )}
        </Box>
      ))}
    </Box>
  );
}
